/**
 * prefetchOfflineData.ts
 *
 * Pre-warms the IndexedDB cache with orders, customers, and products
 * so that the app remains usable even if the network drops immediately
 * after login, before the user has visited any page.
 *
 * Called once when the AdminLayout mounts (i.e. after login).
 * Runs silently in the background — errors are swallowed so this
 * never blocks the UI.
 */

import fetchClient from 'interfaces/fetchClient';
import { offlineDb } from 'utils/offlineDb';

const authConfig = () => ({
    headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
});

/**
 * Fetch the most recent pending orders (last 3 months) and cache them.
 */
async function prefetchOrders(): Promise<void> {
    try {
        const today = new Date();
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(today.getMonth() - 3);
        threeMonthsAgo.setDate(1);
        threeMonthsAgo.setHours(0, 0, 0, 0);

        const endOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        endOfNextMonth.setHours(23, 59, 59, 999);

        const url =
            `/orders?limit=200&page=1&status=pending&search=&isReadyStatus=all&sortBy=deliveryDate` +
            `&start=${threeMonthsAgo.toISOString()}&end=${endOfNextMonth.toISOString()}`;

        const res: any = await fetchClient.get(url, authConfig()).catch(() => null);
        if (!res?.data) return;

        await offlineDb.cacheOrdersList(url, res.data);

        const orders: any[] = res.data?.orders ?? res.data?.data ?? [];
        if (Array.isArray(orders)) {
            await Promise.all(orders.map((o: any) => o?._id && offlineDb.cacheOrder(o)));
        }
    } catch {
        // Fail silently — prefetch must never break the app
    }
}

/**
 * Fetch all active customers and cache them.
 */
async function prefetchCustomers(): Promise<void> {
    try {
        const res: any = await fetchClient
            .get('/customers?limit=2000&page=1', authConfig())
            .catch((): null => null);
        if (!res?.data) return;

        const customers: any[] =
            res.data?.customers ?? res.data?.docs ?? res.data?.data ?? [];
        if (Array.isArray(customers) && customers.length > 0) {
            // Ensure displayName is populated (used by the order search dropdown)
            const normalized = customers.map((c: any) => ({
                ...c,
                displayName: c.displayName || c.company || c.name || '',
            }));
            await offlineDb.cacheCustomers(normalized);
        }
    } catch {
        // Fail silently
    }
}

/**
 * Fetch all active products and cache them individually in the `products` store.
 * Fetches up to 2 000 products (most catalogues fit in one page).
 * Each product is stored by `_id` so getCachedProducts() can serve the full list
 * and getProducts() can filter client-side when offline.
 */
async function prefetchProducts(): Promise<void> {
    try {
        const res: any = await fetchClient
            .get('/products?limit=2000&page=1', authConfig())
            .catch((): null => null);
        if (!res?.data) return;

        const products: any[] =
            res.data?.products ?? res.data?.docs ?? res.data?.data ?? [];
        if (Array.isArray(products) && products.length > 0) {
            await offlineDb.cacheProducts(products);
        }
    } catch {
        // Fail silently
    }
}

/**
 * Main entry point — run all prefetches in parallel.
 * Call this once on app init (AdminLayout mount).
 */
export const prefetchOfflineData = (): void => {
    // Do not block rendering — run in the background
    Promise.all([prefetchOrders(), prefetchCustomers(), prefetchProducts()]).catch(
        () => null,
    );
};
